import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthContext, AuthProvider, type AuthContextProps } from 'react-oidc-context';
import { BrowserRouter } from 'react-router';
import { App } from './App';
import { ApiError } from './api';
import { devSubject, oidcConfig } from './config';
import { I18nProvider } from './i18n';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Client errors (403, 404…) will not fix themselves on retry.
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
    },
  },
});

/** Keycloak in real use; in development with VITE_DEV_SUBJECT, a signed-in stand-in (see config.ts). */
function Auth({ children }: { children: React.ReactNode }) {
  if (!devSubject) return <AuthProvider {...oidcConfig}>{children}</AuthProvider>;
  const dev = { isAuthenticated: true, isLoading: false, user: { access_token: '' }, signoutRedirect: async () => {} } as unknown as AuthContextProps;
  return <AuthContext.Provider value={dev}>{children}</AuthContext.Provider>;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nProvider>
      <Auth>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </Auth>
    </I18nProvider>
  </StrictMode>,
);
